import React, { useState } from 'react';
import { string, object } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import css from './ReportButton.module.css';

const REASONS = ['spam', 'fake', 'inappropriate', 'offensive', 'illegal', 'other'];

/**
 * ReportButton — allows users to report inappropriate content.
 *
 * Usage (in ListingPage, ProfilePage, etc.):
 *   <ReportButton type="listing" targetId={listing.id.uuid} currentUser={currentUser} />
 *   <ReportButton type="user" targetId={authorUser.id.uuid} currentUser={currentUser} />
 *
 * @param {string} props.type — 'listing' | 'user' | 'review'
 * @param {string} props.targetId — UUID of the item to report
 * @param {Object} [props.currentUser] — if null, shows login prompt
 */
const ReportButton = ({ type, targetId, currentUser, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!reason || isLoading) return;
    setIsLoading(true);
    try {
      await fetch('/api/reports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, targetId, reason, comment }),
      });
      setSubmitted(true);
      setIsOpen(false);
    } catch (err) {
      console.error('[ReportButton] error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className={classNames(css.root, className)}>
        <p className={css.thanks}>✓ <FormattedMessage id="ReportButton.thanks" /></p>
      </div>
    );
  }

  if (!currentUser) return null;

  return (
    <div className={classNames(css.root, className)}>
      {!isOpen ? (
        <button type="button" onClick={() => setIsOpen(true)} className={css.trigger}>
          <FormattedMessage id="ReportButton.report" />
        </button>
      ) : (
        <div className={css.form}>
          <p className={css.label}><FormattedMessage id="ReportButton.selectReason" /></p>
          <div className={css.reasons}>
            {REASONS.map(r => (
              <label key={r} className={classNames(css.reasonLabel, reason === r && css.reasonSelected)}>
                <input
                  type="radio"
                  name="report-reason"
                  value={r}
                  checked={reason === r}
                  onChange={() => setReason(r)}
                  className={css.radioInput}
                />
                <FormattedMessage id={`ReportButton.reason.${r}`} />
              </label>
            ))}
          </div>
          <textarea
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder="Additional details (optional)"
            className={css.textarea}
            maxLength={500}
            rows={3}
          />
          <div className={css.actions}>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!reason || isLoading}
              className={css.submitBtn}
            >
              <FormattedMessage id="ReportButton.submit" />
            </button>
            <button type="button" onClick={() => setIsOpen(false)} className={css.cancelBtn}>
              <FormattedMessage id="ReportButton.cancel" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

ReportButton.defaultProps = { currentUser: null, className: null };
ReportButton.propTypes = { type: string.isRequired, targetId: string.isRequired, currentUser: object, className: string };

export default ReportButton;
